import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Box,
  IconButton,
  Divider,
} from "@mui/material";
import axios from "axios";
import Cookies from "js-cookie";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import BusinessIcon from "@mui/icons-material/Business";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import ApartmentIcon from "@mui/icons-material/Apartment";
import ModalLoadingOverlay, {
  modalLoadingContainerSx,
} from "../components/ModalLoadingOverlay";

const DeleteJobDialog = ({
  Deletename,
  id,
  handleClose,
  open,
  setOpenSnackbar,
  setSnackbarMessage,
  setSnackbarServerity,
}) => {
  const [deleteInput, setDeleteInput] = useState("");
  const [deleteButtonDisabled, setDeleteButtonDisabled] = useState(true);
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const isBranchContext =
    searchParams.get("tab") === "BranchInfo" ||
    location.pathname.includes("/BranchDetails/");
  const isCompanyContext =
    searchParams.get("tab") === "companyInfo" && !isBranchContext;

  const handleDeleteInputChange = (event) => {
    const trimmeddeleteName = Deletename.trim();
    const { value } = event.target;
    const trimmedValue = value.trim();
    setDeleteInput(value);
    setDeleteButtonDisabled(trimmedValue !== trimmeddeleteName);
  };

  const handleDeleteJob = () => {
    const parsedId = Number(id);
    if (!Number.isInteger(parsedId)) {
      setSnackbarMessage("Invalid ID");
      setSnackbarServerity("error");
      setOpenSnackbar(true);
      return;
    }

    const payload = {
      id: parsedId,
      entity: isCompanyContext
        ? "company"
        : isBranchContext
          ? "companyBranch"
          : "jobOrder",
    };

    setLoading(true);
    axios
      .post(`/delete-entities`, payload, {
        headers: {
          authorization:
            Cookies.get("_secure_ARJ_") || Cookies.get("_secure_ref"),
        },
      })
      .then((response) => {
        if (response.status === 200) {
          setSnackbarMessage(response.data.message);
          setSnackbarServerity("success");
          setOpenSnackbar(true);
          handleClose();
          setTimeout(() => {
            const redirectPath =
              isCompanyContext || isBranchContext
                ? "/Company?page=1"
                : "/jobOrders?page=1";
            navigate(redirectPath);
          }, 600);
        }
      })
      .catch((err) => {
        setSnackbarMessage(err.response.data.error);
        setSnackbarServerity("error");
        setOpenSnackbar(true);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const getIconCircle = () => {
    const isCompany = isCompanyContext;
    const isBranch = isBranchContext;
    const Icon = isCompany
      ? BusinessIcon
      : isBranch
        ? ApartmentIcon
        : WorkOutlineIcon;

    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          mb: 2.5,
          mt: 1,
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: "50%",
            backgroundColor: "#DBEAFE",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: "50%",
              backgroundColor: "#3B82F6",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Icon sx={{ color: "#fff", fontSize: 26 }} />
          </Box>
        </Box>
      </Box>
    );
  };

  const getWarningTitle = () => {
    if (isCompanyContext) return "If you delete this Company";
    if (isBranchContext) return "If you delete this Branch";
    return "If you delete this Job";
  };

  const getWarningPoints = () => {
    if (isCompanyContext) {
      return [
        "Deleting this company will remove all its associated branches permanently.",
        "All active and inactive jobs under this company will be deleted.",
        "Employee records associated with this company will be permanently erased.",
      ];
    }
    if (isBranchContext) {
      return [
        "All active and inactive jobs under this branch will be deleted.",
        "Employee records associated with this branch will be permanently erased.",
      ];
    }
    return ["All records related to this job will be deleted."];
  };

  const getFieldLabel = () => {
    if (isCompanyContext) return "Company Name";
    if (isBranchContext) return "Branch Name";
    return "Job Name";
  };

  const getDialogTitle = () => {
    if (isCompanyContext) return "Delete Company";
    if (isBranchContext) return "Delete Branch";
    return "Delete Job";
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: modalLoadingContainerSx(loading, {
          borderRadius: "16px",
          px: 1,
          py: 0.5,
        }),
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
          pt: 1,
          px: 3,
        }}
      >
        <Typography
          sx={{ fontSize: "18px", fontWeight: 700, color: "#111827" }}
        >
          {getDialogTitle()}
        </Typography>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{ color: "#6B7280", "&:hover": { backgroundColor: "#F3F4F6" } }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ px: 3, pt: 2.5, pb: 1 }}>
        {getIconCircle()}

        <Typography
          sx={{
            textAlign: "center",
            fontSize: "15px",
            fontWeight: 600,
            color: "#111827",
            mb: 2.5,
            lineHeight: 1.5,
          }}
        >
          Are you sure, you want to delete{" "}
          <Typography
            component="span"
            sx={{ color: "#3B82F6", fontWeight: 600, fontSize: "15px" }}
          >
            {Deletename} ?
          </Typography>
        </Typography>

        {/* Warning Box */}
        <Box
          sx={{
            backgroundColor: "#FEF2F2",
            borderRadius: "10px",
            px: 2.5,
            py: 1.5,
            mb: 2.5,
          }}
        >
          <Typography
            sx={{
              color: "#FF5252",
              fontWeight: 600,
              fontSize: "13.5px",
              mb: 1,
            }}
          >
            {getWarningTitle()}
          </Typography>
          {getWarningPoints().map((point, idx) => (
            <Typography
              key={idx}
              sx={{
                fontSize: "13px",
                color: "#374151",
                display: "flex",
                alignItems: "flex-start",
                gap: "6px",
                mb: 0.5,
                lineHeight: 1.6,
              }}
            >
              <span style={{ marginTop: "1px" }}>•</span>
              <span>{point}</span>
            </Typography>
          ))}
        </Box>

        {/* Confirm text */}
        <Typography sx={{ fontSize: "14px", color: "#374151", mb: 1.5 }}>
          Please type <strong>{Deletename}</strong> to confirm.{" "}
          <Typography
            component="span"
            sx={{ color: "#FF5252", fontWeight: 700 }}
          >
            *
          </Typography>
        </Typography>

        {/* Input */}
        <TextField
          autoFocus
          margin="none"
          placeholder={getFieldLabel()}
          type="text"
          fullWidth
          value={deleteInput}
          onChange={handleDeleteInputChange}
          size="small"
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              fontSize: "14px",
              backgroundColor: "#fff",
            },
            "& .MuiOutlinedInput-input::placeholder": {
              color: "#9CA3AF",
              opacity: 1,
            },
          }}
        />
      </DialogContent>

      {/* ── Actions — small buttons, right aligned, same as screenshot ── */}
      <DialogActions
        sx={{
          px: 3,
          pb: 2.5,
          pt: 1,
          gap: 1,
          justifyContent: "flex-end",
        }}
      >
        {/* Cancel — outlined */}
        <Button
          className="cancel-btn"
          onClick={handleClose}
          variant="outlined"
          sx={{
            textTransform: "uppercase",
            fontWeight: 600,
            fontSize: "13px",
            letterSpacing: "0.5px",
            borderRadius: "8px",
            borderColor: "#D1D5DB",
            color: "#374151",
            px: 2.5,
            py: 0.8,
            boxShadow: "none",
            "&:hover": {
              borderColor: "#9CA3AF",
              backgroundColor: "#F9FAFB",
              boxShadow: "none",
            },
          }}
        >
          Cancel
        </Button>

        {/* Delete — red filled */}
        <Button
          onClick={handleDeleteJob}
          variant="contained"
          disabled={deleteButtonDisabled}
          sx={{
            textTransform: "uppercase",
            fontWeight: 600,
            fontSize: "13px",
            letterSpacing: "0.5px",
            borderRadius: "8px",
            px: 2.5,
            py: 0.8,
            backgroundColor: "#EF4444",
            boxShadow: "none",
            "&:hover": { backgroundColor: "#DC2626", boxShadow: "none" },
            "&.Mui-disabled": { backgroundColor: "#FCA5A5", color: "#fff" },
          }}
        >
          Delete
        </Button>
      </DialogActions>
      <ModalLoadingOverlay open={loading} />
    </Dialog>
  );
};

DeleteJobDialog.propTypes = {
  Deletename: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  handleClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  setOpenSnackbar: PropTypes.func.isRequired,
  setSnackbarMessage: PropTypes.func.isRequired,
  setSnackbarServerity: PropTypes.func.isRequired,
};

export default DeleteJobDialog;
